import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { IModel } from '../../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { LinkedServiceAdapter } from 'app/model/adapters/linkedservices/linkedservice.adapter';
import { passwordOrAKVtoSecretBase, secretBaseToPasswordOrAKV } from 'app/model/adapters/linkedservices/azure-key-vault.util';
import { SalesforceMarketingCloudLinkedService, SalesforceMarketingCloudAuthType } from 'app/model/linkedservice/salesforce-marketing-cloud.linkedservice';
import { arrayIsNullOrEmpty } from 'app/registries/utls/util';
import { salesforceMarketingCloudReservedKeys } from 'app/shared/utils/connector-metadata-static-consts';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(SalesforceMarketingCloudLinkedService),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            type: '*',
            connectVia: '*',
            typeProperties: {
                clientId: '*',
                clientSecret: 'secret',
                useEncryptedEndpoints: '*',
                useHostVerification: '*',
                usePeerVerification: '*',
                encryptedCredential: '*',
                connectionProperties: {
                    host: '*',
                    authenticationType: '*',
                    clientId: '*',
                    clientSecret: 'secret',
                    useEncryptedEndpoints: '*',
                    useHostVerification: '*',
                    usePeerVerification: '*',
                    encryptedCredential: '*'
                }
            }
        }
    }
})
export class SalesforceMarketingCloudLinkedServiceAdapter extends LinkedServiceAdapter<SalesforceMarketingCloudLinkedService> {
    protected modelConstructor(): new () => SalesforceMarketingCloudLinkedService {
        return SalesforceMarketingCloudLinkedService;
    }

    protected model(): IModel {
        return SalesforceMarketingCloudLinkedService.MODEL;
    }

    protected async propertiesToResourceAsync(linkedService: SalesforceMarketingCloudLinkedService, linkedServiceResource: Model.LinkedServiceResource): Promise<Model.LinkedServiceResource> {
        const properties = this.properties(linkedServiceResource) as Model.SalesforceMarketingCloudLinkedService;
        properties.type = Metamodel.modelFor(SalesforceMarketingCloudLinkedService).name;

        if (linkedService.isOldResource) {
            properties.typeProperties = {
                clientId: linkedService.clientId,
                clientSecret: passwordOrAKVtoSecretBase(linkedService.clientSecret, this.context),
                useEncryptedEndpoints: linkedService.useEncryptedEndpoints,
                useHostVerification: linkedService.useEncryptedEndpoints !== false ? linkedService.useHostVerification : undefined,
                usePeerVerification: linkedService.useEncryptedEndpoints !== false ? linkedService.usePeerVerification : undefined
            };
        } else {
            properties.typeProperties = {
                connectionProperties: {
                    host: linkedService.authType === SalesforceMarketingCloudAuthType.OAuth_2_0 ? linkedService.host : linkedService.subdomain,
                    authenticationType: linkedService.authType,
                    clientId: linkedService.clientId,
                    clientSecret: passwordOrAKVtoSecretBase(linkedService.clientSecret, this.context),
                    useEncryptedEndpoints: linkedService.useEncryptedEndpoints,
                    useHostVerification: linkedService.useEncryptedEndpoints !== false ? linkedService.useHostVerification : undefined,
                    usePeerVerification: linkedService.useEncryptedEndpoints !== false ? linkedService.usePeerVerification : undefined
                }
            };
            if (!arrayIsNullOrEmpty(linkedService.additionalFields)) {
                linkedService.additionalFields.forEach((field) => {
                    properties.typeProperties.connectionProperties[field.key] = field.value;
                });
            }
        }

        return linkedServiceResource;
    }

    protected async propertiesToModelAsync(linkedServiceResource: Model.LinkedServiceResource, linkedService: SalesforceMarketingCloudLinkedService): Promise<SalesforceMarketingCloudLinkedService> {
        const properties = linkedServiceResource.properties as Model.SalesforceMarketingCloudLinkedService;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const typeProperties: Model.SalesforceMarketingCloudLinkedServiceTypeProperties = properties.typeProperties;

        if (typeProperties.connectionProperties) {
            linkedService.isOldResource = false;
            const connectionProperties = typeProperties.connectionProperties;
            linkedService.authType = connectionProperties.authenticationType;
            if (linkedService.authType === SalesforceMarketingCloudAuthType.OAuth_2_0) {
                linkedService.host = connectionProperties.host;
            } else {
                linkedService.subdomain = connectionProperties.host;
            }
            linkedService.clientId = connectionProperties.clientId;
            linkedService.clientSecret = secretBaseToPasswordOrAKV(connectionProperties.clientSecret, true, this.context);
            linkedService.useEncryptedEndpoints = connectionProperties.useEncryptedEndpoints;
            linkedService.useHostVerification = connectionProperties.useHostVerification;
            linkedService.usePeerVerification = connectionProperties.usePeerVerification;
            linkedService.additionalFields = [];
            Object.keys(connectionProperties).forEach((key) => {
                if (salesforceMarketingCloudReservedKeys.indexOf(key) < 0) {
                    linkedService.additionalFields.push({ key, value: connectionProperties[key] });
                    delete connectionProperties[key];
                }
            });
        } else {
            linkedService.isOldResource = true;
            linkedService.clientId = typeProperties.clientId;
            linkedService.clientSecret = secretBaseToPasswordOrAKV(typeProperties.clientSecret, true, this.context);
            linkedService.useEncryptedEndpoints = typeProperties.useEncryptedEndpoints;
            linkedService.useHostVerification = typeProperties.useHostVerification;
            linkedService.usePeerVerification = typeProperties.usePeerVerification;
        }

        return linkedService;
    }
}
